import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    ArrowLeftIcon,
    UserIcon,
    BuildingOfficeIcon,
    CalendarIcon,
    EnvelopeIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClipboardDocumentListIcon,
    WrenchScrewdriverIcon,
    ArrowPathIcon,
    ChatBubbleBottomCenterTextIcon,
    ChatBubbleLeftIcon,
    EyeIcon,
    ChevronDownIcon,
    BriefcaseIcon,
    ArrowRightOnRectangleIcon,
    ArrowUturnLeftIcon,
    ClockIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    BellIcon
} from '@heroicons/react/24/outline';
import {
    CheckCircleIcon as CheckCircleIconSolid,
    ClockIcon as ClockIconSolid,
    XCircleIcon as XCircleIconSolid
} from '@heroicons/react/24/solid';
import {
    OffboardingRequestsClient,
    OffboardingRequestDetailsDto,
    RequestStatus,
    RequestItemsClient,
    RequestItemStatus,
    UpdateRequestItemStatusRequest,
    AssignAssetRequest,
    ReassignRequestItemRequest,
    AddRemarkRequest,
    OffboardingAction,
    OffboardingReason
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import { getOffboardingReasonText, getStatusBadgeColor, getRequestStatusText, offboardingReasonOptions } from '../../utils/enumHelpers';
import AssetModal from '../../components/checklist/AssetModal';
import ReassignModal from '../../components/checklist/ReassignModal';
import ActivityLogModal from '../../components/checklist/ActivityLogModal';
import AddRemarkModal from '../../components/checklist/AddRemarkModal';
import AddTaskModal, { AddTaskData } from '../../components/checklist/AddTaskModal';
import { teamsService } from '../../services/teamsService';
import { TeamDto } from '../../web-api-client';
import { getErrorMessage } from '../../utils/errorHelpers';

export default function OffboardingRequestDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [request, setRequest] = useState<OffboardingRequestDetailsDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    // Modal states
    const [assetModal, setAssetModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; currentAsset?: string }>({ isOpen: false });
    const [reassignModal, setReassignModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; currentPicId?: string; currentPicName?: string }>({ isOpen: false });
    const [activityLogModal, setActivityLogModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [remarkModal, setRemarkModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; mode?: 'remark' | 'rejection' }>({ isOpen: false });
    const [confirmCompleteModal, setConfirmCompleteModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; actionLabel?: string }>({ isOpen: false });
    const [pendingStatusChange, setPendingStatusChange] = useState<{ itemId: number; newStatus: RequestItemStatus; offboardingAction?: OffboardingAction } | null>(null);

    // Dropdown state for status selection
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

    // Loading states for async operations
    const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);

    // Edit mode states (for Draft status)
    const [showEditInfoModal, setShowEditInfoModal] = useState(false);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [showRemoveTaskModal, setShowRemoveTaskModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [editLastWorkingDate, setEditLastWorkingDate] = useState('');
    const [editReason, setEditReason] = useState<OffboardingReason>(OffboardingReason.Resigned);
    const [editRemarks, setEditRemarks] = useState('');
    const [savingInfo, setSavingInfo] = useState(false);
    const [deletingTaskId, setDeletingTaskId] = useState<number | null>(null);
    const [discarding, setDiscarding] = useState(false);
    const [teams, setTeams] = useState<TeamDto[]>([]);
    const [sendingReminderId, setSendingReminderId] = useState<number | null>(null);
    const [sendingAllReminders, setSendingAllReminders] = useState(false);
    const [showRemindConfirmModal, setShowRemindConfirmModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [showRemindAllConfirmModal, setShowRemindAllConfirmModal] = useState(false);
    const [showMarkExitedModal, setShowMarkExitedModal] = useState(false);
    const [markingExited, setMarkingExited] = useState(false);

    // Extended date modal state
    const [extendedDateModal, setExtendedDateModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [extendedUntilDate, setExtendedUntilDate] = useState<string>('');

    useEffect(() => {
        const fetchRequestDetails = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const client = new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
                const data = await client.getOffboardingRequestDetails(parseInt(id));
                setRequest(data);
            } catch (error) {
                console.error('Failed to fetch request details:', error);
                toast.error('Failed to load offboarding request details');
            } finally {
                setLoading(false);
            }
        };

        fetchRequestDetails();
    }, [id, refreshKey]);

    // Fetch teams for add task modal
    useEffect(() => {
        const fetchTeams = async () => {
            try {
                const teamsData = await teamsService.getTeams();
                setTeams(teamsData);
            } catch (error) {
                console.error('Failed to fetch teams:', error);
            }
        };
        fetchTeams();
    }, []);

    // Populate edit form when request loads
    useEffect(() => {
        if (request) {
            setEditLastWorkingDate(request.lastWorkingDate ? new Date(request.lastWorkingDate + 'Z').toISOString().split('T')[0] : '');
            setEditReason(request.offboardingReason ?? OffboardingReason.Resigned);
            setEditRemarks(request.remarks ?? '');
        }
    }, [request]);

    const getOffboardingActionBadge = (action: OffboardingAction | undefined | null) => {
        switch (action) {
            case OffboardingAction.Outstanding:
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                        <ClockIcon className="h-3.5 w-3.5" />
                        Outstanding
                    </span>
                );
            case OffboardingAction.Revoked:
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Revoked
                    </span>
                );
            case OffboardingAction.Returned:
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Returned
                    </span>
                );
            case OffboardingAction.Extended:
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        <ClockIcon className="h-3.5 w-3.5" />
                        Extended
                    </span>
                );
            default:
                return null;
        }
    };

    // Handler functions
    const handleStatusUpdate = async (itemId: number, newStatus: RequestItemStatus, remarks?: string, offboardingAction?: OffboardingAction, extendedUntil?: Date) => {
        try {
            setUpdatingStatusId(itemId);
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.updateRequestItemStatus(itemId, new UpdateRequestItemStatusRequest({
                status: newStatus,
                remarks: remarks,
                offboardingAction: offboardingAction,
                extendedUntil: extendedUntil
            }));
            toast.success('Status updated successfully');
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Failed to update status');
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleStatusClick = (itemId: number, itemName: string, newStatus: RequestItemStatus, offboardingAction?: OffboardingAction) => {
        setOpenDropdownId(null);

        // Get action label for display
        const getActionLabel = (action?: OffboardingAction) => {
            switch (action) {
                case OffboardingAction.Revoked: return 'Revoked';
                case OffboardingAction.Returned: return 'Returned';
                case OffboardingAction.Extended: return 'Extended';
                default: return 'Completed';
            }
        };

        // For Extended action, show date picker modal first
        if (newStatus === RequestItemStatus.Completed && offboardingAction === OffboardingAction.Extended) {
            // Set default date to 30 days from today
            const defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 30);
            setExtendedUntilDate(defaultDate.toISOString().split('T')[0]);
            setExtendedDateModal({ isOpen: true, itemId, itemName });
            setPendingStatusChange({ itemId, newStatus, offboardingAction });
            return;
        }

        // For other completion statuses (Revoked, Returned), show confirmation
        if (newStatus === RequestItemStatus.Completed) {
            setConfirmCompleteModal({ isOpen: true, itemId, itemName, actionLabel: getActionLabel(offboardingAction) });
            setPendingStatusChange({ itemId, newStatus, offboardingAction });
            return;
        }

        if (newStatus === RequestItemStatus.Rejected) {
            setRemarkModal({ isOpen: true, itemId, itemName, mode: 'rejection' });
            setPendingStatusChange({ itemId, newStatus, offboardingAction });
            return;
        }

        handleStatusUpdate(itemId, newStatus, undefined, offboardingAction);
    };

    const handleConfirmComplete = () => {
        if (pendingStatusChange) {
            handleStatusUpdate(pendingStatusChange.itemId, pendingStatusChange.newStatus, undefined, pendingStatusChange.offboardingAction);
            setPendingStatusChange(null);
        }
        setConfirmCompleteModal({ isOpen: false });
    };

    const handleConfirmExtended = () => {
        if (pendingStatusChange && extendedUntilDate) {
            const extendedDate = new Date(extendedUntilDate);
            handleStatusUpdate(
                pendingStatusChange.itemId,
                pendingStatusChange.newStatus,
                undefined,
                pendingStatusChange.offboardingAction,
                extendedDate
            );
            setPendingStatusChange(null);
        }
        setExtendedDateModal({ isOpen: false });
        setExtendedUntilDate('');
    };

    const handleAssetAssign = async (assetName: string, assetValue?: string) => {
        if (!assetModal.itemId) return;

        try {
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.assignAsset(assetModal.itemId, new AssignAssetRequest({
                assetName,
                assetValue
            }));
            toast.success('Asset assigned successfully');
            setRefreshKey(k => k + 1);
            setAssetModal({ isOpen: false });
        } catch (error) {
            console.error('Failed to assign asset:', error);
            toast.error('Failed to assign asset');
        }
    };

    const handleReassign = async (newPicId: string, reason?: string) => {
        if (!reassignModal.itemId) return;

        try {
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.reassignRequestItem(reassignModal.itemId, new ReassignRequestItemRequest({
                newPicId,
                reason
            }));
            toast.success('Task reassigned successfully');
            setRefreshKey(k => k + 1);
            setReassignModal({ isOpen: false });
        } catch (error) {
            console.error('Failed to reassign task:', error);
            toast.error('Failed to reassign task');
        }
    };

    const handleAddRemark = async (remark: string) => {
        if (!remarkModal.itemId) return;

        try {
            if (pendingStatusChange) {
                await handleStatusUpdate(pendingStatusChange.itemId, pendingStatusChange.newStatus, remark, pendingStatusChange.offboardingAction);
                setPendingStatusChange(null);
            } else {
                const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
                await client.addRequestItemRemark(remarkModal.itemId, new AddRemarkRequest({ remark }));
                toast.success('Remark added successfully');
                setRefreshKey(k => k + 1);
            }
            setRemarkModal({ isOpen: false });
        } catch (error) {
            console.error('Failed to add remark:', error);
            toast.error('Failed to add remark');
        }
    };

    const handleSubmitOffboarding = async () => {
        if (!id) return;

        try {
            setSubmitting(true);
            const client = new OffboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.submitOffboardingRequest(parseInt(id));
            toast.success('Offboarding request submitted successfully. Notifications have been sent to assigned PICs.');
            setShowSubmitModal(false);
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error('Failed to submit offboarding request:', error);
            toast.error('Failed to submit offboarding request');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle send reminder for a single task - show confirmation first
    const handleRemindClick = (itemId: number, itemName: string) => {
        setShowRemindConfirmModal({ isOpen: true, itemId, itemName });
    };

    // Confirm and send reminder for a single task
    const handleConfirmRemind = async () => {
        if (!showRemindConfirmModal.itemId) return;

        const itemId = showRemindConfirmModal.itemId;
        setShowRemindConfirmModal({ isOpen: false });

        try {
            setSendingReminderId(itemId);
            const response = await fetch(`${API_CONFIG.baseUrl}/api/requestitems/${itemId}/remind`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to send reminder');
            }

            toast.success('Reminder sent successfully');
            setRefreshKey(k => k + 1);
        } catch (error: unknown) {
            console.error('Failed to send reminder:', error);
            toast.error(getErrorMessage(error, 'Failed to send reminder'));
        } finally {
            setSendingReminderId(null);
        }
    };

    // Handle remind all click - show confirmation first
    const handleRemindAllClick = () => {
        setShowRemindAllConfirmModal(true);
    };

    // Confirm and send reminders for all pending tasks
    const handleConfirmRemindAll = async () => {
        if (!id) return;

        setShowRemindAllConfirmModal(false);

        try {
            setSendingAllReminders(true);
            const response = await fetch(`${API_CONFIG.baseUrl}/api/offboardingrequests/${id}/remind-all`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to send reminders');
            }

            const result = await response.json();
            toast.success(`${result.remindersSent} reminder(s) sent successfully`);
            setRefreshKey(k => k + 1);
        } catch (error: unknown) {
            console.error('Failed to send reminders:', error);
            toast.error(getErrorMessage(error, 'Failed to send reminders'));
        } finally {
            setSendingAllReminders(false);
        }
    };

    // Edit handlers for Draft status
    const handleSaveOffboardingInfo = async () => {
        if (!id) return;

        try {
            setSavingInfo(true);
            const response = await createAuthenticatedFetch().fetch(`${API_CONFIG.baseUrl}/api/offboardingrequests/${id}/info`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lastWorkingDate: editLastWorkingDate,
                    reason: editReason,
                    remarks: editRemarks
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update offboarding info');
            }

            toast.success('Offboarding information updated');
            setShowEditInfoModal(false);
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error('Failed to update offboarding info:', error);
            toast.error('Failed to update offboarding information');
        } finally {
            setSavingInfo(false);
        }
    };

    const handleAddTask = async (taskData: AddTaskData) => {
        if (!id) return;

        try {
            const response = await createAuthenticatedFetch().fetch(`${API_CONFIG.baseUrl}/api/requestitems`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    boardingRequestId: parseInt(id),
                    itemName: taskData.taskName,
                    description: taskData.description,
                    templateCategoryId: taskData.categoryId,
                    offboardingAction: OffboardingAction.Outstanding
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add task');
            }

            toast.success('Task added successfully');
            setShowAddTaskModal(false);
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error('Failed to add task:', error);
            toast.error('Failed to add task');
        }
    };

    const handleRemoveTask = async (itemId: number) => {
        try {
            setDeletingTaskId(itemId);
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.removeRequestItem(itemId);
            toast.success('Task removed');
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error('Failed to remove task:', error);
            toast.error('Failed to remove task');
        } finally {
            setDeletingTaskId(null);
        }
    };

    const handleDiscardDraft = async () => {
        if (!id) return;

        try {
            setDiscarding(true);
            const response = await createAuthenticatedFetch().fetch(`${API_CONFIG.baseUrl}/api/offboardingrequests/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to discard draft');
            }

            toast.success('Offboarding draft discarded');
            navigate('/offboarding');
        } catch (error) {
            console.error('Failed to discard draft:', error);
            toast.error('Failed to discard draft');
        } finally {
            setDiscarding(false);
            setShowDiscardModal(false);
        }
    };

    // Handle mark employee as exited
    const handleMarkEmployeeAsExited = async () => {
        if (!id) return;

        try {
            setMarkingExited(true);
            const response = await createAuthenticatedFetch().fetch(`${API_CONFIG.baseUrl}/api/offboardingrequests/${id}/mark-exited`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to mark employee as exited');
            }

            toast.success('Employee has been marked as exited successfully');
            setShowMarkExitedModal(false);
            setRefreshKey(k => k + 1);
        } catch (error: unknown) {
            console.error('Failed to mark employee as exited:', error);
            toast.error(getErrorMessage(error, 'Failed to mark employee as exited'));
        } finally {
            setMarkingExited(false);
        }
    };

    // Combined status badge that shows user-friendly status based on RequestItemStatus + OffboardingAction
    const getCombinedStatusBadge = (status: RequestItemStatus, offboardingAction: OffboardingAction | undefined | null) => {
        const baseClasses = "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg shadow-sm";

        // If rejected, show "Rejected"
        if (status === RequestItemStatus.Rejected) {
            return (
                <span className={`${baseClasses} bg-red-100 text-red-800 border-2 border-red-400`}>
                    <XCircleIconSolid className="h-5 w-5" />
                    Rejected
                </span>
            );
        }

        // Show badge based on offboardingAction
        switch (offboardingAction) {
            case OffboardingAction.Revoked:
                return (
                    <span className={`${baseClasses} bg-green-100 text-green-800 border-2 border-green-500`}>
                        <CheckCircleIconSolid className="h-5 w-5" />
                        Revoked
                    </span>
                );
            case OffboardingAction.Returned:
                return (
                    <span className={`${baseClasses} bg-green-100 text-green-800 border-2 border-green-500`}>
                        <CheckCircleIconSolid className="h-5 w-5" />
                        Returned
                    </span>
                );
            case OffboardingAction.Extended:
                return (
                    <span className={`${baseClasses} bg-blue-100 text-blue-800 border-2 border-blue-500`}>
                        <ClockIconSolid className="h-5 w-5" />
                        Extended
                    </span>
                );
            case OffboardingAction.Outstanding:
            default:
                return (
                    <span className={`${baseClasses} bg-yellow-100 text-yellow-800 border-2 border-yellow-400`}>
                        <ClockIconSolid className="h-5 w-5" />
                        Outstanding
                    </span>
                );
        }
    };

    // Keep original function for dropdown compatibility
    const getItemStatusBadge = (status: RequestItemStatus) => {
        const baseClasses = "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg shadow-sm";

        switch (status) {
            case RequestItemStatus.Pending:
                return (
                    <span className={`${baseClasses} bg-yellow-100 text-yellow-800 border-2 border-yellow-400`}>
                        <ClockIconSolid className="h-5 w-5" />
                        Outstanding
                    </span>
                );
            case RequestItemStatus.Completed:
                return (
                    <span className={`${baseClasses} bg-green-100 text-green-800 border-2 border-green-500`}>
                        <CheckCircleIconSolid className="h-5 w-5" />
                        Done
                    </span>
                );
            case RequestItemStatus.Rejected:
                return (
                    <span className={`${baseClasses} bg-red-100 text-red-800 border-2 border-red-400`}>
                        <XCircleIconSolid className="h-5 w-5" />
                        Rejected
                    </span>
                );
            default:
                return (
                    <span className={`${baseClasses} bg-gray-100 text-gray-800 border-2 border-gray-300`}>
                        Unknown
                    </span>
                );
        }
    };

    // Status options for offboarding tasks - combines RequestItemStatus with OffboardingAction
    const getStatusOptions = () => [
        { value: RequestItemStatus.Pending, label: 'Outstanding', icon: ClockIconSolid, className: 'text-yellow-800', offboardingAction: OffboardingAction.Outstanding },
        { value: RequestItemStatus.Completed, label: 'Revoked', icon: CheckCircleIconSolid, className: 'text-green-800', offboardingAction: OffboardingAction.Revoked },
        { value: RequestItemStatus.Completed, label: 'Returned', icon: CheckCircleIconSolid, className: 'text-green-800', offboardingAction: OffboardingAction.Returned },
        { value: RequestItemStatus.Completed, label: 'Extended', icon: ClockIconSolid, className: 'text-blue-800', offboardingAction: OffboardingAction.Extended },
        { value: RequestItemStatus.Rejected, label: 'Rejected', icon: XCircleIconSolid, className: 'text-red-800', offboardingAction: undefined }
    ];

    const renderStatusDropdown = (itemId: number, itemName: string, currentStatus: RequestItemStatus, currentOffboardingAction?: OffboardingAction) => {
        const isOpen = openDropdownId === itemId;
        const statusOptions = getStatusOptions();
        const currentOption = statusOptions.find(opt => opt.value === currentStatus);
        const isUpdating = updatingStatusId === itemId;

        // Extended items can always change status, other completed/rejected items are finalized
        const isExtended = currentOffboardingAction === OffboardingAction.Extended;
        const isFinalized = (currentStatus === RequestItemStatus.Completed || currentStatus === RequestItemStatus.Rejected) && !isExtended;

        if (isFinalized) {
            return (
                <div className="inline-block cursor-not-allowed" title="Status is finalized">
                    {getItemStatusBadge(currentStatus)}
                </div>
            );
        }

        return (
            <div className="relative inline-block">
                <button
                    onClick={() => !isUpdating && setOpenDropdownId(isOpen ? null : itemId)}
                    disabled={isUpdating}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isUpdating ? (
                        <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                    ) : (
                        currentOption && <currentOption.icon className={`h-5 w-5 ${currentOption.className}`} />
                    )}
                    <span>{isUpdating ? 'Updating...' : 'Change Status'}</span>
                    {!isUpdating && <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                </button>

                {isOpen && (
                    <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)} />
                        <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                            {statusOptions.map((option, index) => {
                                return (
                                    <button
                                        key={option.label}
                                        onClick={() => {
                                            handleStatusClick(itemId, itemName, option.value, option.offboardingAction);
                                        }}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-gray-50 cursor-pointer
                                            ${index === 0 ? 'rounded-t-lg' : ''}
                                            ${index === statusOptions.length - 1 ? 'rounded-b-lg' : ''}
                                            border-b border-gray-100 last:border-b-0`}
                                    >
                                        <option.icon className={`h-5 w-5 ${option.className} flex-shrink-0`} />
                                        <span className={`font-medium ${option.className}`}>{option.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500">Loading offboarding request details...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (!request) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex flex-col items-center justify-center py-12">
                        <XCircleIcon className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-gray-600 font-medium">Offboarding request not found</p>
                        <button
                            onClick={() => navigate('/offboarding')}
                            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            <ArrowLeftIcon className="mr-2 h-4 w-4" />
                            Back to Offboarding
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={() => navigate('/offboarding')}
                        className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeftIcon className="mr-2 h-4 w-4" />
                        Back to Offboarding
                    </button>
                    <div className="flex flex-col items-end gap-2">
                        {request.status !== undefined && request.status !== null && (
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(request.status)}`}>
                                {getRequestStatusText(request.status)}
                            </span>
                        )}
                        {/* Progress Indicator */}
                        {(() => {
                            const totalTasks = request.totalTasks || 0;
                            const completedTasks = request.completedTasks || 0;
                            const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

                            return (
                                <div className="w-48">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs font-medium text-gray-600">Progress</span>
                                        <span className="text-xs font-semibold text-gray-700">{progress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className="bg-gradient-to-r from-red-500 to-orange-500 h-1.5 rounded-full transition-all duration-300"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{completedTasks} / {totalTasks} tasks</p>
                                </div>
                            );
                        })()}
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <ArrowRightOnRectangleIcon className="h-8 w-8 text-red-600" />
                        Offboarding Request Details
                    </h1>
                    <div className="flex items-center gap-3">
                        {/* Submit Button - only show for Draft status */}
                        {request.status === RequestStatus.Draft && (
                            <button
                                onClick={() => setShowSubmitModal(true)}
                                disabled={submitting}
                                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl shadow-lg hover:shadow-xl text-white font-medium transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <>
                                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                                        Submit & Notify PICs
                                    </>
                                )}
                            </button>
                        )}
                        {/* Mark Employee as Exited Button - show when all tasks are completed and status is InProgress */}
                        {request.status === RequestStatus.InProgress && (() => {
                            const totalTasks = request.totalTasks || 0;
                            const completedTasks = request.completedTasks || 0;
                            const allTasksCompleted = totalTasks > 0 && completedTasks === totalTasks;

                            if (allTasksCompleted) {
                                return (
                                    <button
                                        onClick={() => setShowMarkExitedModal(true)}
                                        disabled={markingExited}
                                        className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-900 rounded-xl shadow-lg hover:shadow-xl text-white font-medium transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {markingExited ? (
                                            <>
                                                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                                                Mark Employee as Exited
                                            </>
                                        )}
                                    </button>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>

            {/* Offboarding Info */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                        <ArrowRightOnRectangleIcon className="h-6 w-6 mr-2 text-red-600" />
                        Offboarding Information
                    </h2>
                    {/* Edit button for Draft status */}
                    {request.status === RequestStatus.Draft && (
                        <button
                            onClick={() => setShowEditInfoModal(true)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit Info
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-start">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Last Working Date</label>
                            <p className="text-base font-semibold text-gray-900">
                                {request.lastWorkingDate ? new Date(request.lastWorkingDate + 'Z').toLocaleDateString() : 'Not set'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Reason</label>
                            <p className="text-base font-semibold text-gray-900">{getOffboardingReasonText(request.offboardingReason)}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Submitted By</label>
                            <p className="text-base font-semibold text-gray-900">{request.submittedBy || '-'}</p>
                        </div>
                    </div>
                </div>
                {request.remarks && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Remarks</label>
                        <p className="text-sm text-gray-700">{request.remarks}</p>
                    </div>
                )}

                {/* Action Summary */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-600">{request.revokeCount || 0}</p>
                        <p className="text-sm text-red-700">Revoked</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{request.returnCount || 0}</p>
                        <p className="text-sm text-amber-700">Returned</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{request.extendedCount || 0}</p>
                        <p className="text-sm text-blue-700">Extended</p>
                    </div>
                </div>
            </div>

            {/* Employee Information */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <UserIcon className="h-6 w-6 mr-2 text-red-600" />
                    Employee Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.fullName}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Designation</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.designation}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.locationName}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Department</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.departmentName}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Personal Email</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.personalEmail}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Employment Date</label>
                            <p className="text-base font-semibold text-gray-900">
                                {request.employee?.employmentDate ? new Date(request.employee.employmentDate + 'Z').toLocaleDateString() : '-'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Offboarding Tasks */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                        <ClipboardDocumentListIcon className="h-6 w-6 mr-2 text-red-600" />
                        Offboarding Tasks
                    </h2>
                    <div className="flex items-center gap-3">
                        {/* Remind All - Only show when request is InProgress and there are pending tasks */}
                        {request.status === RequestStatus.InProgress && (() => {
                            const pendingTaskCount = request.categories?.flatMap(c => c.items || [])
                                .filter(item => item.status !== RequestItemStatus.Completed &&
                                               item.status !== RequestItemStatus.Rejected).length || 0;

                            if (pendingTaskCount > 0) {
                                return (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">
                                            {pendingTaskCount} Upcoming Task{pendingTaskCount !== 1 ? 's' : ''}
                                        </span>
                                        <button
                                            onClick={handleRemindAllClick}
                                            disabled={sendingAllReminders}
                                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <BellIcon className="h-4 w-4 mr-1" />
                                            {sendingAllReminders ? 'Sending...' : 'Remind All'}
                                        </button>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        {/* Add Task and Discard Draft buttons for Draft status */}
                        {request.status === RequestStatus.Draft && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowAddTaskModal(true)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                    <PlusIcon className="h-4 w-4 mr-1" />
                                    Add Task
                                </button>
                                <button
                                    onClick={() => setShowDiscardModal(true)}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                    <TrashIcon className="h-4 w-4 mr-1" />
                                    Discard Draft
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {request.categories && request.categories.length > 0 ? (
                    <div className="space-y-6">
                        {request.categories.map((category) => (
                            <div key={category.categoryId} className="border rounded-xl overflow-visible">
                                <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3">
                                    <h3 className="text-lg font-semibold text-white">{category.categoryName}</h3>
                                </div>
                                <div className="divide-y divide-gray-200">
                                    {category.items?.map((item) => (
                                        <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="text-lg font-medium text-gray-900">{item.itemName}</h4>
                                                        {item.extendedUntil && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                                                <CalendarIcon className="h-3 w-3" />
                                                                Until {new Date(item.extendedUntil + 'Z').toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {item.description && (
                                                        <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    {getCombinedStatusBadge(item.status ?? RequestItemStatus.Pending, item.offboardingAction)}
                                                    {/* Remind button - show only for non-completed tasks when request is InProgress */}
                                                    {request.status === RequestStatus.InProgress &&
                                                     item.status !== RequestItemStatus.Completed &&
                                                     item.status !== RequestItemStatus.Rejected && (
                                                        <button
                                                            onClick={() => handleRemindClick(item.id!, item.itemName ?? '')}
                                                            disabled={sendingReminderId === item.id}
                                                            className="text-xs text-amber-600 hover:text-amber-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                        >
                                                            <BellIcon className="h-3 w-3" />
                                                            {sendingReminderId === item.id ? 'Sending...' : 'Remind'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div className="flex items-center text-sm">
                                                    <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                    <span className="text-gray-600">Assignee:</span>
                                                    <span className="ml-2 font-medium text-gray-900">
                                                        {item.assignedTeamName ? `${item.assignedTeamName}` :
                                                         item.assignedPersonName ? `${item.assignedPersonName}` :
                                                         'Unassigned'}
                                                    </span>
                                                </div>
                                                {item.assignedAsset && (
                                                    <div className="flex items-center text-sm">
                                                        <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                        <span className="ml-2 font-medium text-gray-900">{item.assignedAsset}</span>
                                                    </div>
                                                )}
                                                {item.latestRemark && (
                                                    <div className="flex flex-col text-sm">
                                                        <div className="flex items-center">
                                                            <ChatBubbleLeftIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                            <span className="text-gray-600">Latest Remark:</span>
                                                            <span className="ml-2 text-gray-900">{item.latestRemark}</span>
                                                        </div>
                                                        {item.latestRemarkDate && (
                                                            <div className="text-xs text-gray-500 italic mt-1 ml-6">
                                                                Updated on {new Date(item.latestRemarkDate + 'Z').toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex flex-wrap gap-2">
                                                {renderStatusDropdown(item.id!, item.itemName ?? '', item.status ?? RequestItemStatus.Pending, item.offboardingAction)}

                                                <button
                                                    onClick={() => setAssetModal({
                                                        isOpen: true,
                                                        itemId: item.id,
                                                        itemName: item.itemName ?? '',
                                                        currentAsset: item.assignedAsset ?? undefined
                                                    })}
                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                                                >
                                                    <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                                                    {item.assignedAsset ? 'Update Asset' : 'Assign Asset'}
                                                </button>

                                                {item.status !== RequestItemStatus.Completed && item.status !== RequestItemStatus.Rejected && (
                                                    <button
                                                        onClick={() => setReassignModal({
                                                            isOpen: true,
                                                            itemId: item.id,
                                                            itemName: item.itemName ?? '',
                                                            currentPicId: item.picId ?? undefined,
                                                            currentPicName: item.assignedPersonName ?? undefined
                                                        })}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                                                    >
                                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                                        Reassign
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => setRemarkModal({
                                                        isOpen: true,
                                                        itemId: item.id,
                                                        itemName: item.itemName ?? ''
                                                    })}
                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                                                >
                                                    <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mr-1" />
                                                    Add Remark
                                                </button>

                                                <button
                                                    onClick={() => setActivityLogModal({
                                                        isOpen: true,
                                                        itemId: item.id,
                                                        itemName: item.itemName ?? ''
                                                    })}
                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                                                >
                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                    View History
                                                </button>

                                                {/* Delete task button for Draft status */}
                                                {request.status === RequestStatus.Draft && (
                                                    <button
                                                        onClick={() => setShowRemoveTaskModal({
                                                            isOpen: true,
                                                            itemId: item.id,
                                                            itemName: item.itemName ?? ''
                                                        })}
                                                        disabled={deletingTaskId === item.id}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {deletingTaskId === item.id ? (
                                                            <>
                                                                <div className="animate-spin h-4 w-4 border-2 border-red-300 border-t-red-600 rounded-full mr-1"></div>
                                                                Removing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TrashIcon className="h-4 w-4 mr-1" />
                                                                Remove
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <CheckCircleIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No tasks assigned yet</p>
                    </div>
                )}
            </div>

            {/* Modals */}
            <AssetModal
                isOpen={assetModal.isOpen}
                onClose={() => setAssetModal({ isOpen: false })}
                onSubmit={handleAssetAssign}
                currentAsset={assetModal.currentAsset}
                taskName={assetModal.itemName ?? ''}
            />

            <ReassignModal
                isOpen={reassignModal.isOpen}
                onClose={() => setReassignModal({ isOpen: false })}
                onSubmit={handleReassign}
                currentPicId={reassignModal.currentPicId}
                currentPicName={reassignModal.currentPicName}
                taskName={reassignModal.itemName ?? ''}
            />

            <ActivityLogModal
                isOpen={activityLogModal.isOpen}
                onClose={() => setActivityLogModal({ isOpen: false })}
                requestItemId={activityLogModal.itemId ?? 0}
                taskName={activityLogModal.itemName ?? ''}
            />

            <AddRemarkModal
                isOpen={remarkModal.isOpen}
                onClose={() => {
                    setRemarkModal({ isOpen: false });
                    setPendingStatusChange(null);
                }}
                onSubmit={handleAddRemark}
                taskName={remarkModal.itemName ?? ''}
                mode={remarkModal.mode}
            />

            {/* Confirm Status Change Modal */}
            {confirmCompleteModal.isOpen && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Mark as {confirmCompleteModal.actionLabel}
                                </h3>
                                <p className="text-sm text-gray-600 mb-1">
                                    Are you sure you want to mark "<strong>{confirmCompleteModal.itemName}</strong>" as <strong>{confirmCompleteModal.actionLabel}</strong>?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => {
                                    setConfirmCompleteModal({ isOpen: false });
                                    setPendingStatusChange(null);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmComplete}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Yes, {confirmCompleteModal.actionLabel}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Extended Date Selection Modal */}
            {extendedDateModal.isOpen && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <ClockIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Set Extended Period
                                </h3>
                                <p className="text-sm text-gray-600 mb-1">
                                    Select until when "<strong>{extendedDateModal.itemName}</strong>" should be extended.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Extended Until Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={extendedUntilDate}
                                    onChange={(e) => setExtendedUntilDate(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    The item/access will remain active until this date.
                                </p>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm text-blue-800">
                                    <strong>Note:</strong> You can update the status anytime after marking as extended,
                                    regardless of the extended date.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => {
                                    setExtendedDateModal({ isOpen: false });
                                    setExtendedUntilDate('');
                                    setPendingStatusChange(null);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmExtended}
                                disabled={!extendedUntilDate}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Confirm Extended
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Submit & Notify PICs Modal */}
            {showSubmitModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <ArrowRightOnRectangleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Submit & Notify PICs
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Ready to start the offboarding process?
                                </p>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-sm text-amber-800">
                                        <strong>What happens next:</strong>
                                    </p>
                                    <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                                        <li>Status changes from "Draft" to "In Progress"</li>
                                        <li>Email notifications sent to all assigned PICs</li>
                                        <li>PICs can start completing their assigned tasks</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowSubmitModal(false)}
                                disabled={submitting}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitOffboarding}
                                disabled={submitting}
                                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-orange-600 rounded-lg hover:from-red-700 hover:to-orange-700 transition-colors disabled:opacity-50 inline-flex items-center"
                            >
                                {submitting ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit & Notify PICs'
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Edit Offboarding Info Modal */}
            {showEditInfoModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <PencilIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Edit Offboarding Information
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Update the offboarding details for this request.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* Last Working Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Working Date
                                </label>
                                <input
                                    type="date"
                                    value={editLastWorkingDate}
                                    onChange={(e) => setEditLastWorkingDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Offboarding Reason */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reason for Offboarding
                                </label>
                                <select
                                    value={editReason}
                                    onChange={(e) => setEditReason(parseInt(e.target.value) as OffboardingReason)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {offboardingReasonOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Remarks */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Remarks
                                </label>
                                <textarea
                                    value={editRemarks}
                                    onChange={(e) => setEditRemarks(e.target.value)}
                                    rows={3}
                                    placeholder="Add any additional notes or remarks..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowEditInfoModal(false)}
                                disabled={savingInfo}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveOffboardingInfo}
                                disabled={savingInfo}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 inline-flex items-center"
                            >
                                {savingInfo ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Add Task Modal */}
            <AddTaskModal
                isOpen={showAddTaskModal}
                onClose={() => setShowAddTaskModal(false)}
                onSave={handleAddTask}
                title="Add Offboarding Task"
            />

            {/* Discard Draft Confirmation Modal */}
            {showDiscardModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <TrashIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Discard Draft
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Are you sure you want to discard this offboarding draft?
                                </p>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-sm text-red-800">
                                        <strong>Warning:</strong> This action cannot be undone. All tasks and information in this draft will be permanently deleted.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowDiscardModal(false)}
                                disabled={discarding}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDiscardDraft}
                                disabled={discarding}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center"
                            >
                                {discarding ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Discarding...
                                    </>
                                ) : (
                                    'Discard Draft'
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Remove Task Confirmation Modal */}
            {showRemoveTaskModal.isOpen && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <TrashIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Remove Task
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to remove "<strong>{showRemoveTaskModal.itemName}</strong>" from this offboarding request?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowRemoveTaskModal({ isOpen: false })}
                                disabled={deletingTaskId !== null}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    if (showRemoveTaskModal.itemId) {
                                        await handleRemoveTask(showRemoveTaskModal.itemId);
                                        setShowRemoveTaskModal({ isOpen: false });
                                    }
                                }}
                                disabled={deletingTaskId !== null}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center"
                            >
                                {deletingTaskId === showRemoveTaskModal.itemId ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Removing...
                                    </>
                                ) : (
                                    'Yes, Remove'
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Remind Single Task Confirmation Modal */}
            {showRemindConfirmModal.isOpen && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <BellIcon className="h-8 w-8 text-amber-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Send Reminder
                                </h3>
                                <p className="text-sm text-gray-600">
                                    This will send an email to the assignee for task "<strong>{showRemindConfirmModal.itemName}</strong>". Are you sure you want to send a reminder?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowRemindConfirmModal({ isOpen: false })}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmRemind}
                                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                            >
                                Yes, Send Reminder
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Remind All Tasks Confirmation Modal */}
            {showRemindAllConfirmModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <BellIcon className="h-8 w-8 text-amber-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Send Reminders to All Assignees
                                </h3>
                                <p className="text-sm text-gray-600">
                                    This will send an email to all assignees with pending tasks. Are you sure you want to remind all?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowRemindAllConfirmModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmRemindAll}
                                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                            >
                                Yes, Remind All
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Mark Employee as Exited Confirmation Modal */}
            {showMarkExitedModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <ArrowRightOnRectangleIcon className="h-8 w-8 text-gray-700" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Mark Employee as Exited
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Are you sure you want to mark <strong>{request?.employee?.fullName}</strong> as exited?
                                </p>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-sm text-amber-800">
                                        <strong>What happens:</strong>
                                    </p>
                                    <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                                        <li>Employee status changes to "Exited"</li>
                                        <li>Employee moves to "Exited Employees" list</li>
                                        <li>Offboarding request marked as completed</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowMarkExitedModal(false)}
                                disabled={markingExited}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMarkEmployeeAsExited}
                                disabled={markingExited}
                                className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 inline-flex items-center"
                            >
                                {markingExited ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Processing...
                                    </>
                                ) : (
                                    'Yes, Mark as Exited'
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
